import React from 'react';
import { Zap, CheckCircle2, Search, FileText, ClipboardList, Receipt, Layers, PenTool, BookOpen, AlertTriangle, Flag } from 'lucide-react';
type OrderBumpsProps = {
  bump1Checked: boolean;
  setBump1Checked: (val: boolean) => void;
  bump2Checked: boolean;
  setBump2Checked: (val: boolean) => void;
};
export function OrderBumps({
  bump1Checked,
  setBump1Checked,
  bump2Checked,
  setBump2Checked
}: OrderBumpsProps) {
  return <div className="space-y-6 mb-10" data-id="element-468">
      {/* ORDER BUMP #1 */}
      <div className={`relative border-2 border-dashed rounded-2xl p-6 md:p-8 transition-all order-bump-flash ${bump1Checked ? 'border-brand-gold bg-brand-cream/80' : 'border-gray-300 bg-white'}`} data-id="element-469">
        {/* Badge */}
        <div className="flex justify-center -mt-10 mb-4" data-id="element-470">
          <div className="inline-flex items-center gap-1.5 bg-brand-gold text-brand-dark font-bold text-xs uppercase tracking-wider px-4 py-1.5 rounded-full shadow-md" data-id="element-471">
            <Zap className="w-3.5 h-3.5" data-id="element-472" />
            ONE-TIME OFFER — JUST $9 TODAY
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6" data-id="element-473">
          {/* Image */}
          <div className="flex-shrink-0 w-full md:w-48" data-id="element-474">
            <img src="https://cdn.magicpatterns.com/uploads/bQVWYTcZdEemZ2RAcqcXX9/Order_Bump_1_Image_The_Senior_Benefit.png" alt="The Hidden Wealth Retirement Audit" className="w-full h-auto rounded-xl shadow-lg" data-id="element-475" />
          </div>

          {/* Content */}
          <div className="flex-1" data-id="element-476">
            <h4 className="font-serif text-xl md:text-2xl font-bold text-brand-dark mb-2 leading-snug" data-id="element-477">
              "What if your biggest paycheck is already sitting in your name...
              unclaimed?"
            </h4>
            <p className="text-gray-700 text-base leading-relaxed mb-4" data-id="element-478">
              Most seniors focus only on their monthly expenses — and miss the
              large sources of accumulated wealth that legally belong to them
              and have been sitting dormant for decades. We are talking about
              pension credits from old jobs, forgotten insurance refunds,
              inactive bank accounts, or specific senior tax deductions that
              most accountants overlook every single year.
            </p>
            <p className="text-gray-700 text-base leading-relaxed mb-4" data-id="element-479">
              <strong className="text-brand-dark" data-id="element-480">
                The Hidden Wealth Retirement Audit
              </strong>{' '}
              is a step-by-step audit designed specifically for adults 60+ to
              systematically check every one of these overlooked wealth sources
              and find out, once and for all, if money is sitting in your name
              right now.
            </p>

            <p className="text-sm font-bold uppercase tracking-wider text-brand-gold mb-3" data-id="element-481">
              Inside this resource:
            </p>
            <div className="space-y-2 mb-4" data-id="element-482">
              <div className="flex items-start gap-2" data-id="element-483">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-484" />
                <p className="text-sm text-gray-700" data-id="element-485">
                  <strong className="text-brand-dark" data-id="element-486">
                    The 7-Source Hidden Wealth Checklist:
                  </strong>{' '}
                  A systematic review of unclaimed pensions, veteran
                  entitlements, and dormant assets.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-487">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-488" />
                <p className="text-sm text-gray-700" data-id="element-489">
                  <strong className="text-brand-dark" data-id="element-490">
                    The Unclaimed Property Database Guide:
                  </strong>{' '}
                  Exactly how to search national and state databases for money
                  already registered in your name.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-491">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-492" />
                <p className="text-sm text-gray-700" data-id="element-493">
                  <strong className="text-brand-dark" data-id="element-494">
                    The Pension Trace Worksheet:
                  </strong>{' '}
                  Ideal if you worked multiple jobs over a 40-year career and
                  never confirmed if you were paid everything owed to you.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-495">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-496" />
                <p className="text-sm text-gray-700" data-id="element-497">
                  <strong className="text-brand-dark" data-id="element-498">
                    The Senior Tax Deduction Audit:
                  </strong>{' '}
                  The high-value tax benefits your accountant probably isn't
                  telling you about.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4" data-id="element-499">
              <span className="text-gray-500 text-base" data-id="element-500">Regular Price:</span>
              <span className="text-gray-400 line-through text-lg" data-id="element-501">$47.00</span>
              <span className="text-brand-dark font-bold text-base" data-id="element-502">
                Have it today for just
              </span>
              <span className="text-brand-gold font-black text-2xl" data-id="element-503">$9.00</span>
            </div>

            <p className="text-sm text-gray-600 italic mb-4" data-id="element-504">
              The Main Guide finds your monthly credits. The Hidden Wealth
              Retirement Audit finds the money most seniors never think to look
              for.
            </p>

            {/* Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group" data-id="element-505">
              <div className={`w-7 h-7 flex-shrink-0 rounded-md border-2 flex items-center justify-center transition-all mt-0.5 ${bump1Checked ? 'bg-brand-gold border-brand-gold' : 'border-gray-400 bg-white group-hover:border-brand-gold'}`} onClick={() => setBump1Checked(!bump1Checked)} data-id="element-506">
                {bump1Checked && <svg className="w-4 h-4 text-brand-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-507">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-508" />
                  </svg>}
              </div>
              <span className="text-base font-bold text-brand-dark leading-snug" data-id="element-509">
                YES — Add "The Hidden Wealth Retirement Audit" to my order for
                just $9.
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* ORDER BUMP #2 */}
      <div className={`relative border-2 border-dashed rounded-2xl p-6 md:p-8 transition-all order-bump-flash ${bump2Checked ? 'border-brand-gold bg-brand-cream/80' : 'border-gray-300 bg-white'}`} data-id="element-510">
        {/* Badge */}
        <div className="flex justify-center -mt-10 mb-4" data-id="element-511">
          <div className="inline-flex items-center gap-1.5 bg-brand-gold text-brand-dark font-bold text-xs uppercase tracking-wider px-4 py-1.5 rounded-full shadow-md" data-id="element-512">
            <Zap className="w-3.5 h-3.5" data-id="element-513" />
            ONE-TIME OFFER — JUST $9 TODAY
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-6" data-id="element-514">
          {/* Image */}
          <div className="flex-shrink-0 w-full md:w-48" data-id="element-515">
            <img src="https://cdn.magicpatterns.com/uploads/thYDoEocWVeeYpqXbTozs6/Order_Bump_2_Image_The_Senior_Benefit.png" alt="The Priority Advocacy Master System" className="w-full h-auto rounded-xl shadow-lg" data-id="element-516" />
          </div>

          {/* Content */}
          <div className="flex-1" data-id="element-517">
            <h4 className="font-serif text-xl md:text-2xl font-bold text-brand-dark mb-2 leading-snug" data-id="element-518">
              "What happens if they say NO? Here is exactly what to do next."
            </h4>
            <p className="text-gray-700 text-base leading-relaxed mb-4" data-id="element-519">
              You followed the steps. You filled out the application. You
              submitted every document. And yet... they denied you. Or worse,
              they stopped responding entirely. Most people accept a "No" as the
              final answer. They are wrong.
            </p>
            <p className="text-gray-700 text-base leading-relaxed mb-4" data-id="element-520">
              There is a formal system of advocacy and escalation — made up of
              real government offices with real authority — that exists
              specifically to overturn wrongful denials and force agencies to
              respond. Almost nobody uses this system because almost nobody
              knows it exists.
            </p>
            <p className="text-gray-700 text-base leading-relaxed mb-4" data-id="element-521">
              <strong className="text-brand-dark" data-id="element-522">
                The Priority Advocacy Master System
              </strong>{' '}
              is your strategic playbook for moving through every level of this
              system — in the correct order — without needing a lawyer and
              without spending a single dollar.
            </p>

            <p className="text-sm font-bold uppercase tracking-wider text-brand-gold mb-3" data-id="element-523">
              Inside this resource:
            </p>
            <div className="space-y-2 mb-4" data-id="element-524">
              <div className="flex items-start gap-2" data-id="element-525">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-526" />
                <p className="text-sm text-gray-700" data-id="element-527">
                  <strong className="text-brand-dark" data-id="element-528">
                    The 5-Level Escalation Ladder:
                  </strong>{' '}
                  A clear path from your first denial all the way to
                  Congressional constituent services.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-529">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-530" />
                <p className="text-sm text-gray-700" data-id="element-531">
                  <strong className="text-brand-dark" data-id="element-532">
                    The Formal Appeal Script Library:
                  </strong>{' '}
                  Word-for-word scripts for writing appeal letters and making
                  effective escalation calls.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-533">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-534" />
                <p className="text-sm text-gray-700" data-id="element-535">
                  <strong className="text-brand-dark" data-id="element-536">
                    The Senior Advocacy Office Directory:
                  </strong>{' '}
                  Key contacts for ombudsmen, SHIP Medicare counselors, and free
                  legal aid for seniors.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-537">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-538" />
                <p className="text-sm text-gray-700" data-id="element-539">
                  <strong className="text-brand-dark" data-id="element-540">
                    The "They Said No" Response Protocol:
                  </strong>{' '}
                  How to identify whether your denial was a program error or a
                  documentation issue — and how to correct it.
                </p>
              </div>
              <div className="flex items-start gap-2" data-id="element-541">
                <CheckCircle2 className="w-5 h-5 text-brand-gold flex-shrink-0 mt-0.5" data-id="element-542" />
                <p className="text-sm text-gray-700" data-id="element-543">
                  <strong className="text-brand-dark" data-id="element-544">
                    The Congressional Services Script:
                  </strong>{' '}
                  How to contact your local representatives so their staff can
                  help you navigate federal agency problems.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-4" data-id="element-545">
              <span className="text-gray-500 text-base" data-id="element-546">Regular Price:</span>
              <span className="text-gray-400 line-through text-lg" data-id="element-547">$47.00</span>
              <span className="text-brand-dark font-bold text-base" data-id="element-548">
                Have it today for just
              </span>
              <span className="text-brand-gold font-black text-2xl" data-id="element-549">$9.00</span>
            </div>

            <p className="text-sm text-gray-600 italic mb-4" data-id="element-550">
              The main guide gets you to the application. The Priority Advocacy
              Master System ensures that if they say "no," you know exactly how
              to fight back and win.
            </p>

            {/* Checkbox */}
            <label className="flex items-start gap-3 cursor-pointer group" data-id="element-551">
              <div className={`w-7 h-7 flex-shrink-0 rounded-md border-2 flex items-center justify-center transition-all mt-0.5 ${bump2Checked ? 'bg-brand-gold border-brand-gold' : 'border-gray-400 bg-white group-hover:border-brand-gold'}`} onClick={() => setBump2Checked(!bump2Checked)} data-id="element-552">
                {bump2Checked && <svg className="w-4 h-4 text-brand-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-553">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-554" />
                  </svg>}
              </div>
              <span className="text-base font-bold text-brand-dark leading-snug" data-id="element-555">
                YES — Add "The Priority Advocacy Master System" to my order for
                just $9.
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>;
}